import React, { useEffect } from 'react';
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Music, Zap, Headphones, AudioWaveform, ChevronRight } from 'lucide-react';
import { latestUpdates } from '../constants/updates';

const Home = () => {
    const { isAuthenticated, isLoading } = useAuth0();
    const navigate = useNavigate();

    useEffect(() => {
        if (!isAuthenticated && !isLoading) {
            navigate('/');
            return;
        }
    }, [isAuthenticated, isLoading, navigate]);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="w-8 h-8 border-4 border-muted border-t-primary rounded-full animate-spin"></div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return null;
    }

    // latestUpdates теперь импортируются из constants

    const quickActions = [
        {
            title: "Fast Track",
            description: "Turn a loop or sketch into a whole production",
            icon: Zap,
            link: "/catalogue"
        },
        {
            title: "Generate Songs",
            description: "Use our soundworlds to create ideas and instrumentals",
            icon: Music,
            link: "/explore"
        },
        {
            title: "Train Soundworld",
            description: "Make your own custom instrumental models that sound like you",
            icon: Headphones,
            link: "/train"
        },
        {
            title: "Audio Utilities",
            description: "Mastering, stem separation, chord detection",
            icon: AudioWaveform,
            link: "/audio-utilities"
        }
    ];

    return (
        <div className="flex-1 bg-background p-4 pt-12 md:pt-16 overflow-y-auto">
            <div className="max-w-6xl mx-auto">
                {/* Welcome Section */ }
                <div className="mb-6">
                    <h1 className="text-2xl font-bold text-foreground mb-1">Welcome back!</h1>
                    <p className="text-sm text-muted-foreground">What would you like to do today?</p>
                </div>

                {/* Latest Updates Section */ }
                <div className="mb-8">
                    <h2 className="text-lg font-bold text-foreground mb-4">Latest Updates</h2>
                    <div className="relative">
                        <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide snap-x snap-mandatory">
                            { latestUpdates.map((update) => (
                                <div key={ update.id } className="snap-start flex-shrink-0 w-64 h-72 bg-card rounded-lg border border-border overflow-hidden hover:shadow-md transition-shadow flex flex-col">
                                    <div className="h-36 overflow-hidden">
                                        <img
                                            src={ update.image }
                                            alt={ update.title }
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                    <div className="p-4 flex flex-col gap-2 flex-1 min-h-0">
                                        <h3 className="text-sm font-bold text-card-foreground min-h-[2rem]">{ update.title }</h3>
                                        <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2 min-h-[2.5rem]">{ update.description }</p>
                                        <Link to={ `/updates/${update.id}` }>
                                            <Button variant="outline" size="sm" className="w-full flex items-center gap-1 text-xs mt-auto">
                                                <Music size={ 12 } />
                                                { update.buttonText }
                                            </Button>
                                        </Link>
                                    </div>
                                </div>
                            )) }
                        </div>
                    </div>
                </div>

                {/* Quick Actions Section */ }
                <div>
                    <h2 className="text-lg font-bold text-foreground mb-4">Quick Start</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        { quickActions.map((action, index) => (
                            <Link key={ index } to={ action.link }>
                                <div className="bg-card rounded-lg border border-border p-4 hover:shadow-md transition-all duration-200 group cursor-pointer h-24">
                                    <div className="flex items-center justify-between h-full min-h-0">
                                        <div className="flex items-center gap-3">
                                            <div className="w-9 h-9 flex items-center justify-center bg-accent rounded-md">
                                                <action.icon size={ 18 } className="text-accent-foreground" />
                                            </div>
                                            <div className="flex flex-col justify-center overflow-hidden">
                                                <h3 className="text-sm font-bold text-card-foreground leading-none truncate">{ action.title }</h3>
                                                <p className="text-xs text-muted-foreground mt-1 leading-snug line-clamp-2">{ action.description }</p>
                                            </div>
                                        </div>
                                        <ChevronRight size={ 16 } className="text-muted-foreground group-hover:text-foreground transition-colors" />
                                    </div>
                                </div>
                            </Link>
                        )) }
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Home;
